// MARK://dhk8/admin-shell/pages/modules/SuppliersPage
// Compras y Proveedores — lista de proveedores → dhk8-erp-suppliers.
// Lista DS: DataTable + FilterBar (búsqueda + filtro de estado) + SummaryStrip.
// Alta/edición vía SectionedSheet; estado como StatusPill (Inactivo = rojo,
// lifecycleTone); borrar con useConfirm; toggle activar/desactivar cableado al
// updateSupplier. Clic en la fila → /suppliers/:id. Conserva curvas/tokens.
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, Truck, ChevronRight, Power } from 'lucide-react'
import * as sup from '../../lib/clients/suppliers.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import DataTable, { type DataTableColumn, type SortState } from '../../components/DataTable.js'
import FilterBar from '../../components/FilterBar.js'
import { SummaryStrip } from '../../components/StatCard.js'
import StatusPill, { lifecycleTone } from '../../components/StatusPill.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import { useToast } from '../../components/Toast.js'

interface SupplierForm {
  legalName: string
  rfc: string
  type: sup.SupplierType
  networkId: string
  active: boolean
}
const emptyForm = (): SupplierForm => ({ legalName: '', rfc: '', type: 'COMPANY', networkId: '', active: true })

type StatusFilter = 'all' | 'active' | 'inactive'

export default function SuppliersPage() {
  const navigate = useNavigate()
  const confirm = useConfirm()
  const { toast } = useToast()
  const list = useAsync((s) => sup.listSuppliers(s))

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<sup.Supplier | null>(null)
  const [form, setForm] = useState<SupplierForm>(emptyForm())
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  // Proveedor con un toggle activar/desactivar en vuelo.
  const [togglingId, setTogglingId] = useState<string | null>(null)

  // Filtros locales (la lista llega completa del grafo, limit 100).
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [sort, setSort] = useState<SortState | null>({ key: 'legalName', dir: 'asc' })

  function openCreate() { setForm(emptyForm()); setFormError(null); setCreating(true) }
  function openEdit(s: sup.Supplier) {
    setForm({ legalName: s.legalName, rfc: s.rfc ?? '', type: s.type, networkId: s.networkId ?? '', active: s.active })
    setFormError(null); setEditing(s)
  }

  async function saveCreate() {
    setFormError(null)
    if (!form.legalName.trim()) { setFormError('La razón social es obligatoria.'); return }
    setSaving(true)
    try {
      await sup.createSupplier({
        legalName: form.legalName.trim(),
        ...(form.rfc.trim() ? { rfc: form.rfc.trim() } : {}),
        type: form.type,
        ...(form.networkId.trim() ? { networkId: form.networkId.trim() } : {}),
      })
      setCreating(false); list.reload()
      toast('Proveedor creado', 'success')
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'No se pudo crear el proveedor.')
    } finally { setSaving(false) }
  }

  async function saveEdit() {
    if (!editing) return
    setFormError(null)
    if (!form.legalName.trim()) { setFormError('La razón social es obligatoria.'); return }
    setSaving(true)
    try {
      await sup.updateSupplier(editing.id, {
        legalName: form.legalName.trim(),
        rfc: form.rfc.trim() ? form.rfc.trim() : null,
        type: form.type,
        active: form.active,
      })
      setEditing(null); list.reload()
      toast('Proveedor actualizado', 'success')
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'No se pudo actualizar el proveedor.')
    } finally { setSaving(false) }
  }

  async function remove(s: sup.Supplier) {
    const ok = await confirm({
      title: '¿Eliminar proveedor?',
      message: `Se eliminará "${s.legalName}". Esta acción no se puede deshacer.`,
      tone: 'danger', confirmLabel: 'Eliminar',
    })
    if (!ok) return
    try { await sup.deleteSupplier(s.id); list.reload(); toast('Proveedor eliminado', 'success') }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar el proveedor.', 'danger') }
  }

  async function toggleActive(s: sup.Supplier) {
    setTogglingId(s.id)
    try {
      await sup.updateSupplier(s.id, { active: !s.active })
      list.reload()
      toast(s.active ? 'Proveedor desactivado' : 'Proveedor activado', 'success')
    }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo cambiar el estado del proveedor.', 'danger') }
    finally { setTogglingId(null) }
  }

  // ── filtro + orden client-side ──
  const rows = useMemo(() => {
    const all = list.data ?? []
    const q = search.trim().toLowerCase()
    let out = all.filter((s) => {
      if (status === 'active' && !s.active) return false
      if (status === 'inactive' && s.active) return false
      if (!q) return true
      return s.legalName.toLowerCase().includes(q) || (s.rfc ?? '').toLowerCase().includes(q)
    })
    if (sort) {
      const dir = sort.dir === 'asc' ? 1 : -1
      out = [...out].sort((a, b) => {
        const av = sort.key === 'rfc' ? (a.rfc ?? '') : sort.key === 'type' ? sup.supplierTypeLabel(a.type) : a.legalName
        const bv = sort.key === 'rfc' ? (b.rfc ?? '') : sort.key === 'type' ? sup.supplierTypeLabel(b.type) : b.legalName
        return av.localeCompare(bv, 'es-MX') * dir
      })
    }
    return out
  }, [list.data, search, status, sort])

  const totalCount = list.data?.length ?? 0
  const activeCount = (list.data ?? []).filter((s) => s.active).length
  const inactiveCount = totalCount - activeCount

  const columns: DataTableColumn<sup.Supplier>[] = [
    {
      key: 'legalName', header: 'Razón social', sortable: true,
      render: (s) => (
        <span>
          <Truck size={14} aria-hidden style={{ verticalAlign: 'middle', marginRight: 6, opacity: 0.6 }} />
          {s.legalName}
        </span>
      ),
    },
    { key: 'rfc', header: 'RFC', sortable: true, render: (s) => <span className="adm-mono">{s.rfc ?? '—'}</span> },
    { key: 'type', header: 'Tipo', sortable: true, render: (s) => sup.supplierTypeLabel(s.type) },
    { key: 'network', header: 'Red', render: (s) => (s.networkId ? <span className="adm-muted">enlazado</span> : '—') },
    {
      key: 'estado', header: 'Estado',
      render: (s) => <StatusPill tone={lifecycleTone(s.active)} label={s.active ? 'Activo' : 'Inactivo'} />,
    },
    {
      key: 'acciones', header: '', align: 'right', width: '1%',
      render: (s) => (
        <div style={{ display: 'inline-flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => toggleActive(s)} disabled={togglingId === s.id} aria-label={s.active ? 'Desactivar' : 'Activar'} title={s.active ? 'Desactivar proveedor' : 'Activar proveedor'}><Power size={15} /></button>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => openEdit(s)} aria-label="Editar" title="Editar"><Pencil size={15} /></button>
          <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => remove(s)} aria-label="Eliminar" title="Eliminar"><Trash2 size={15} /></button>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate(`/suppliers/${s.id}`)} aria-label="Ver ficha" title="Ver ficha"><ChevronRight size={16} /></button>
        </div>
      ),
    },
  ]

  // El form se agrupa por densidad. `active` solo es editable en edición:
  // la mutation de alta (ErpSupplierCreateInput) no acepta `active` —
  // el alta lo deja activo por defecto. email/phone/tags/archived/direcciones
  // NO los acepta el grafo todavía (van a la ficha read-only / flagged).
  const renderFields = (mode: 'create' | 'edit') => (
    <>
      {formError && <div className="adm-banner adm-banner--danger" role="alert">{formError}</div>}

      <FormSection title="Datos fiscales">
        <label className="adm-field">
          <span className="adm-field__label">Razón social</span>
          <input className="adm-input" value={form.legalName} onChange={(e) => setForm({ ...form, legalName: e.target.value })} placeholder="Cafés de Altura SA de CV" />
        </label>
        <label className="adm-field">
          <span className="adm-field__label">RFC</span>
          <input className="adm-input" value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })} placeholder="CAO260514AB1" />
        </label>
        <label className="adm-field">
          <span className="adm-field__label">Tipo</span>
          <select className="adm-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as sup.SupplierType })} aria-label="Tipo de proveedor">
            <option value="COMPANY">Persona moral</option>
            <option value="INDIVIDUAL">Persona física</option>
            <option value="FOREIGN">Extranjero</option>
          </select>
        </label>
      </FormSection>

      {mode === 'edit' && (
        <FormSection title="Estado">
          <label className="adm-field">
            <span className="adm-field__label">Estado</span>
            <select className="adm-select" value={form.active ? 'active' : 'inactive'} onChange={(e) => setForm({ ...form, active: e.target.value === 'active' })} aria-label="Estado del proveedor">
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
            </select>
          </label>
        </FormSection>
      )}
    </>
  )

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div><h1 className="adm-page__title">Proveedores</h1><p className="adm-page__sub">Directorio de proveedores y datos fiscales</p></div>
      </div>

      {(totalCount > 0 || list.loading) && (
        <div style={{ marginBottom: 'var(--pos-space-3, 12px)' }}>
          <SummaryStrip items={[
            { label: 'Proveedores', value: totalCount },
            { label: 'Activos', value: activeCount, tone: 'ok' },
            { label: 'Inactivos', value: inactiveCount, tone: inactiveCount > 0 ? 'danger' : 'neutral' },
          ]} />
        </div>
      )}

      {asyncState(list.loading, list.error, list.reload)}

      {list.data && (
        <DataTable
          columns={columns}
          rows={rows}
          getRowKey={(s) => s.id}
          zebra
          sort={sort}
          onSortChange={setSort}
          onRowClick={(s) => navigate(`/suppliers/${s.id}`)}
          emptyText={search || status !== 'all' ? 'Sin proveedores que coincidan con el filtro.' : 'Sin proveedores. Da de alta uno para empezar a comprar.'}
          toolbar={
            <FilterBar
              search={search}
              onSearch={setSearch}
              searchPlaceholder="Buscar por razón social o RFC…"
              filters={[{
                key: 'status', label: 'Estado', value: status,
                onChange: (v) => setStatus(v as StatusFilter),
                options: [
                  { value: 'all', label: 'Todos' },
                  { value: 'active', label: 'Activos' },
                  { value: 'inactive', label: 'Inactivos' },
                ],
              }]}
              actions={<button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openCreate}><Plus size={16} /> Nuevo proveedor</button>}
            />
          }
        />
      )}

      <SectionedSheet
        open={creating}
        title="Nuevo proveedor"
        onClose={() => { setCreating(false); setFormError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setCreating(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveCreate} disabled={saving || !form.legalName.trim()}>{saving ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >{renderFields('create')}</SectionedSheet>

      <SectionedSheet
        open={editing != null}
        title="Editar proveedor"
        onClose={() => { setEditing(null); setFormError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setEditing(null)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveEdit} disabled={saving || !form.legalName.trim()}>{saving ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >{renderFields('edit')}</SectionedSheet>
    </div>
  )
}
